import * as React from "react";
import { Box } from "../../components/ui/box";
import { Icon } from "../../components/ui/icon";
import { Input } from "../../components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "../../components/ui/input-group";
import { useEntitySelector } from "./entity-selector-context";
import type { Omittable } from "../../lib/props";
import { cn } from "../../lib/utils";
import { useOptionalAutoFocus } from "../../hooks/use-open-focus";

export type EntitySelectorInputProps = Omit<
  React.ComponentProps<typeof Input>,
  "value" | "onChange" | "unstyled" | "bare" | "className" | "style" | "variant"
> & Omittable<{
  placeholder?: string;
  /** Focus on mount. Unset, the field takes the caret where a hardware
   *  keyboard is implied and waits for its own tap on a coarse pointer
   *  (`useOptionalAutoFocus`). */
  autoFocus?: boolean;
  /** Hide the leading search glyph (shown by default). */
  hideIcon?: boolean;
  /**
   * Presentation. Omit for the framed well pinned above a selector list.
   * `header`: the flush command-palette row — a borderless field spanning the
   * popup's full width with a hairline under it, for an overlay whose top row
   * IS the search field rather than a title.
   */
  variant?: "header";
  /**
   * Header row only: paint the band on the muted wash so the search reads as
   * its own tier above the list (two-tone popup). Off, the band shares the
   * popup fill and the hairline alone separates it.
   */
  tinted?: boolean;
  /**
   * Tokens standing in the field with the typed query — the current
   * selections rendered as removable chips (a token field). In the framed well
   * they take a full-width row of their own above the query line (a
   * block-start addon); in the `header` row they sit inline with the query and
   * the band wraps as they accumulate. The search glyph is dropped while
   * tokens show — a token field has no leading column for it.
   */
  leading?: React.ReactNode;
  /**
   * Backspace with nothing left to delete in the query. A token field pops its
   * last token here; without it the key is inert on an empty well.
   */
  onBackspaceEmpty?: () => void;
  /**
   * Enter with a query but no painted row — nothing matched. A picker that can
   * mint what was typed creates it here; without it Enter does nothing on an
   * empty match list. Enter on a painted row still picks the row.
   */
  onSearchSubmit?: (query: string) => void;
  ref?: React.Ref<HTMLInputElement>;
}>;

/**
 * The framed search well pinned at the top of the selector list: a real input
 * field inset from the popup edges, carrying the standard input hairline and
 * focus ring — it reads as "type here", not as another list row. Query state
 * lives in the root; this renders it and writes back on input.
 */
function EntitySelectorInput({
  placeholder,
  autoFocus,
  hideIcon = false,
  variant,
  tinted = false,
  leading,
  onBackspaceEmpty,
  onSearchSubmit,
  ref: forwardedRef,
  onKeyDown: consumerKeyDown,
  ...props
}: EntitySelectorInputProps) {
  const resolvedAutoFocus = useOptionalAutoFocus(autoFocus);
  const {
    search,
    setSearch,
    config,
    inputRef,
    moveActive,
    selectActive,
    cursorVisible,
    activeId,
    variant: selectorVariant,
  } = useEntitySelector();

  // Default keyboard nav: arrows drive the cursor, Enter picks the painted
  // row. Wired here because this input IS the picker's own — a host that
  // forwards these keys itself (`useSelectorKeyboardNav` spread onto this
  // input) preventDefaults first, and its handled keys are skipped. Enter is
  // consumed only while the cursor paints, so an enclosing form keeps its
  // submit when there is no visible target.
  const handleKeyDown = React.useCallback(
    // Base UI's enriched keyboard event (`preventBaseUIHandler` and friends),
    // taken from the prop so a consumer handler can be called with it as-is.
    (event: Parameters<NonNullable<EntitySelectorInputProps["onKeyDown"]>>[0]) => {
      consumerKeyDown?.(event);
      if (event.defaultPrevented) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        moveActive(1);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        moveActive(-1);
      } else if (event.key === "Enter") {
        // A painted cursor with a row under it: Enter picks the row. A live
        // query paints the cursor even when nothing matches, so the row's
        // existence — not the paint — decides between pick and submit.
        if (cursorVisible && activeId !== null) {
          event.preventDefault();
          selectActive();
        } else if (onSearchSubmit && search.trim().length > 0) {
          event.preventDefault();
          onSearchSubmit(search.trim());
        }
      } else if (
        event.key === "Backspace" &&
        onBackspaceEmpty &&
        search.length === 0
      ) {
        event.preventDefault();
        onBackspaceEmpty();
      }
    },
    [
      consumerKeyDown,
      moveActive,
      selectActive,
      cursorVisible,
      activeId,
      onSearchSubmit,
      onBackspaceEmpty,
      search,
    ],
  );

  const mergedRef = React.useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [forwardedRef, inputRef],
  );

  const resolvedPlaceholder =
    placeholder ?? config.searchPlaceholder ?? "Search...";
  // A token field passes "" once tokens stand in it — the chips say what the
  // field holds. The field still needs a NAME, so the accessible label falls
  // back to the config's rather than going silent with the placeholder.
  const accessibleName =
    resolvedPlaceholder || (config.searchPlaceholder ?? "Search...");
  const popoverScale = selectorVariant === "popover";

  if (variant === "header") {
    return (
      // On the picker's 16px content line, so the glyph sits on the same rail
      // as the rows and the footer buttons below the list. The field carries
      // no frame of its own — the row is the frame, and the hairline under it
      // is what separates header from list. With tokens the band keeps its
      // floor and wraps: chips lead, the query takes what is left of the line
      // and drops to its own once they crowd it.
      //
      // Scale follows the popup: inside a popover the search reads at the
      // rows' 14px on a 40px band — 16px side inset, or 10px all round once
      // tokens stand in it, which is the height a 20px chip row lands on
      // anyway, so the band never changes height between the two states; the
      // inline mount — the command-palette dialog — keeps its 16px on 52px.
      <Box
        align="center"
        className={cn(
          "border-b",
          popoverScale
            ? leading
              ? "min-h-10 p-2.5"
              : "min-h-10 px-4"
            : cn("px-4", leading && "min-h-13 py-2"),
          tinted && "bg-muted/60 dark:bg-input/30",
        )}
        direction="row"
        wrap={!!leading}
        gap={leading ? "xs" : "sm"}
        shrink={false}
      >
        {leading ??
          (!hideIcon && <Icon name="search" size="sm" color="muted" />)}
        <Input
          bare
          ref={mergedRef}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={resolvedPlaceholder}
          // The explicit role replaces the native input mapping, and with it
          // the placeholder's job as the name of last resort — a caller's own
          // `aria-label` still wins from `props`.
          aria-label={accessibleName}
          autoFocus={resolvedAutoFocus}
          autoComplete="off"
          spellCheck={false}
          role="searchbox"
          onKeyDown={handleKeyDown}
          className={cn(
            "min-w-0 bg-transparent outline-none",
            popoverScale
              ? "text-sm placeholder:text-muted-foreground"
              : "text-base placeholder:text-muted-foreground/72",
            leading
              ? cn("min-w-24 flex-1", popoverScale ? "h-5" : "h-9")
              : cn("flex-1", popoverScale ? "h-10" : "h-13"),
          )}
          {...props}
        />
      </Box>
    );
  }

  return (
    // 6px inset from the popup edges on every side — the edge the row washes
    // below share, so the well's frame and every wash line up. The list below
    // starts with its own label/row rhythm, so the well still reads as pinned
    // chrome rather than the first row.
    <Box className="p-1.5" shrink={false}>
      <InputGroup variant="outline">
        {leading ? (
          // The token row: chips wrap onto as many lines as they need, on the
          // same 8px inset the query line keeps, with the query underneath.
          <InputGroupAddon
            align="block-start"
            className="flex-wrap gap-1 pt-2 pb-0"
          >
            {leading}
          </InputGroupAddon>
        ) : (
          !hideIcon && (
            <InputGroupAddon>
              <Icon name="search" size="sm" color="muted" />
            </InputGroupAddon>
          )
        )}
        {/* Default size: the 32px field rung, matching the 32px rows below. */}
        <InputGroupInput
          ref={mergedRef}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={resolvedPlaceholder}
          // The explicit role replaces the native input mapping, and with it
          // the placeholder's job as the name of last resort — so a field that
          // reads "Search people" on screen announced as an unnamed searchbox.
          // A caller's own `aria-label` still wins from `props`.
          aria-label={accessibleName}
          autoFocus={resolvedAutoFocus}
          autoComplete="off"
          spellCheck={false}
          role="searchbox"
          onKeyDown={handleKeyDown}
          {...props}
        />
      </InputGroup>
    </Box>
  );
}

EntitySelectorInput.displayName = "EntitySelectorInput";

export { EntitySelectorInput };
